import { useMemo } from 'react';
import { Box, Chip, LinearProgress, Tooltip, Typography } from '@mui/material';

/**
 * Descripciones del workflow de estados (mostradas en tooltip al hover
 * 2s sobre el chip de estado). Si el codigo no esta en el mapa, se
 * muestra la descripcion fallback "Estado del ticket".
 */
const ESTADO_TOOLTIPS: Record<string, string> = {
  nuevo:
    'NUEVO: el ticket recién fue creado y está en la bandeja del supervisor del área. Aún no ha sido asignado a una persona específica.',
  abierto:
    'ABIERTO: el ticket ya tiene un usuario asignado y está en atención. Cuando el trabajo termina, se cierra con el botón "Cerrar Ticket" (solución y motivo de cierre).',
  en_proceso:
    'EN PROCESO: estado retirado del flujo actual (los tickets que lo tenían pasaron a Abierto).',
  pendiente_cliente:
    'PENDIENTE CLIENTE: estado retirado del flujo actual (los tickets que lo tenían pasaron a Abierto).',
  resuelto:
    'RESUELTO: estado retirado del flujo actual (los tickets que lo tenían pasaron a Cerrado).',
  cerrado:
    'CERRADO: el ticket fue cerrado con solución, categoría y subcategoría de cierre. Es un estado final.',
  cancelado:
    'CANCELADO: el ticket fue descartado (por error, duplicado, cliente desistió, etc.). Es un estado final sin solución técnica.',
};
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import dayjs from 'dayjs';

import { ITicket } from '@/shared/interfaces/erp/tickets-soporte-clientes';
import { useSlaEnabled } from '@/shared/hooks/erp/tickets/useSlaEnabled';

type HeaderTicketProps = {
  ticket: ITicket;
};

const getSlaColor = (percentage: number): string => {
  if (percentage < 50) return '#4caf50';
  if (percentage < 75) return '#ff9800';
  if (percentage < 90) return '#ed6c02';
  return '#d32f2f';
};

export default function HeaderTicket({ ticket }: HeaderTicketProps) {
  const { slaEnabled } = useSlaEnabled();
  const slaProgress = useMemo(() => {
    if (!ticket.fecha_limite_sla) return 0;

    const creacion = dayjs(ticket.fecha_creacion);
    const limite = dayjs(ticket.fecha_limite_sla);
    const ahora = dayjs();

    const totalMinutos = limite.diff(creacion, 'minute');
    if (totalMinutos <= 0) return 100;

    const minutosTranscurridos = ahora.diff(creacion, 'minute');
    const porcentaje = (minutosTranscurridos / totalMinutos) * 100;

    return Math.min(Math.max(porcentaje, 0), 100);
  }, [ticket.fecha_creacion, ticket.fecha_limite_sla]);

  const slaColor = getSlaColor(slaProgress);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" fontWeight="bold">
          {ticket.codigo_ticket}
        </Typography>

        {ticket.estado_ticket_data && (
          <Tooltip
            title={
              ESTADO_TOOLTIPS[(ticket.estado_ticket_data.codigo || '').toLowerCase()]
              || 'Estado del ticket'
            }
            enterDelay={2000}
            arrow
            placement="bottom"
          >
            <Chip
              label={ticket.estado_ticket_data.nombre}
              size="small"
              sx={{
                backgroundColor: ticket.estado_ticket_data.color,
                color: '#fff',
                fontWeight: 600,
                cursor: 'help',
              }}
            />
          </Tooltip>
        )}

        {ticket.prioridad_data && (
          <Chip
            label={ticket.prioridad_data.nombre}
            size="small"
            variant="outlined"
            sx={{
              borderColor: ticket.prioridad_data.color,
              color: ticket.prioridad_data.color,
              fontWeight: 600,
            }}
          />
        )}

        {ticket.tipo_data && (
          <Chip
            label={ticket.tipo_data.nombre}
            size="small"
            variant="outlined"
            color="default"
          />
        )}

        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
          label={dayjs(ticket.fecha_creacion).format('DD/MM/YYYY HH:mm')}
          size="small"
          variant="outlined"
          color="default"
        />
      </Box>

      {slaEnabled && ticket.fecha_limite_sla && (
        <Box sx={{ minWidth: 250, maxWidth: 350, flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              SLA
            </Typography>
            <Typography variant="caption" fontWeight="bold" sx={{ color: slaColor }}>
              {Math.round(slaProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={slaProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundColor: slaColor,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Vence: {dayjs(ticket.fecha_limite_sla).format('DD/MM/YYYY HH:mm')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
